/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo, useRef } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useAdditionalFields } from "../lib/react-query/useAdditionalFields";
import LoadingIndicator from "../components/LoadingIndicator";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import Form from "../components/ui/form/Form";
import Button from "../components/ui/Button";
import FormFieldsWrapper from "../components/ui/form/FormFieldsWrapper";
import FormGroup from "../components/ui/form/FormGroup";
import { useBookingMutation } from "../hooks/useBookingMutation";
import { sanitizeFormValues } from "../utils/helpers/sanitizeValues";
import AdditionalField from "../components/booking/AdditionalField";
import FormError from "../components/ui/form/FormError";
import { useBooking } from "../hooks/useBooking";
import { buildProductsForRequest } from "../store/slices/bookingSlice";
import CaptchaWidget from "../components/booking/CaptchaWidget";
import { useCaptcha } from "../hooks/useCaptcha";
import usePluginUtils from "../hooks/usePluginUtils";

const StyledAdditionalFieldsForm = styled((props) => <Form {...props} />)`
  margin-top: 8px;
  padding: 24px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 8px;
`;

const AdditionalFields = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm();
  const { selectedServices, updateBooking, bookingRequest } = useBooking();
  const { handleBookingMutation, isPending } = useBookingMutation();
  const { isPluginEnabled } = usePluginUtils();
  const packageEnabled = isPluginEnabled("package");
  const { challengeData, isReady, refreshChallenge } = useCaptcha();
  const captchaRef = useRef(null);
  const {
    isLoading,
    isError,
    data: additional_fields,
  } = useAdditionalFields({
    "filter[service_id]": selectedServices.map((service) => service.id),
  });

  const sanitizedFields = useMemo(
    () =>
      additional_fields.map((field) => ({
        ...field,
        default_value: sanitizeFormValues(field.default_value),
      })),
    [additional_fields]
  );

  if (isLoading) {
    return (
      <Container>
        <NavigationHeader location back="step" />
        <LoadingIndicator page />
      </Container>
    );
  }

  if (isError) {
    return;
  }

  const onSubmit = async (data) => {
    let captchaToken = null;
    if (challengeData && isReady) {
      captchaToken = captchaRef.current ? await captchaRef.current.getToken() : null;
      if (!captchaToken) {
        setError("root", { message: "Please complete the captcha" });
        return;
      }
    }

    const additionalFieldsData = Object.keys(data).map((key) => ({
      field: key,
      value: data[key],
    }));

    updateBooking({ additional_fields: additionalFieldsData });

    const bookingObject = {
      ...bookingRequest,
      additional_fields: additionalFieldsData,
      products: buildProductsForRequest(selectedServices),
      ...(captchaToken ? { captcha_token: captchaToken } : {}),
    };

    handleBookingMutation(bookingObject, setError, { onError: refreshChallenge });
  };

  return (
    <Container>
      <NavigationHeader location back="step" />
      <StyledAdditionalFieldsForm onSubmit={handleSubmit(onSubmit)}>
        <FormFieldsWrapper>
          {sanitizedFields?.map((field) => (
            <AdditionalField key={field.id} field={field} control={control} errors={errors} />
          ))}
        </FormFieldsWrapper>
        {challengeData && isReady && (
          <div style={{ marginBottom: '16px' }}>
            <FormGroup>
              <CaptchaWidget ref={captchaRef} challenge={challengeData} />
            </FormGroup>
          </div>
        )}
        <Button variant="primary" title={packageEnabled ? "continue" : "book_btn_title"} loading={isPending} />
        {errors.root && <FormError>{errors.root.message}</FormError>}
      </StyledAdditionalFieldsForm>
    </Container>
  );
};

export default AdditionalFields;
